import { Component, inject, OnInit, Optional, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { BehaviorSubject, firstValueFrom, Subject, Subscription } from 'rxjs';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { CustomTableSelectComponent } from 'src/app/components/custom-table-select/custom-table-select.component';
import { CustomListDropdown } from 'src/app/interfaces/custom-list/custom-list-dropdown.interface';
import { ModalObjectType } from 'src/app/interfaces/modal-object-types.interface';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { StorePriceModel } from 'src/app/models/subrental/store-price.model';
import { ArticleSelectionTableModel } from 'src/app/models/tables/archive/article-selection-table.model';
import { ArticleService } from 'src/app/services/archive/article.service';
import { CostCenterPriceService } from 'src/app/services/archive/cost-center-price.service';
import { StoreService } from 'src/app/services/archive/store.service';
import { WindowService } from 'src/app/services/authentication/window.service';
import { FormControlStateService } from 'src/app/services/global-services/form-control-state.service';
import { BranchService } from 'src/app/services/management/branch.service';
import { StorePriceService } from 'src/app/services/subrental/store-price.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-store-price',
  templateUrl: './register-store-price.component.html',
  styleUrls: ['./register-store-price.component.scss'],
  standalone: false
})
export class RegisterStorePriceComponent implements OnInit {

  private readonly branchService = inject(BranchService);
  public readonly config = inject(DynamicDialogConfig);
  private readonly costCenterPriceService = inject(CostCenterPriceService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly formControlStateService = inject(FormControlStateService);
  private readonly storeService = inject(StoreService);
  private readonly storePriceService = inject(StorePriceService);
  private readonly toastHandlerService = inject(ToastHandlerService);
  private readonly windowService = inject(WindowService);
  private readonly articleService = inject(ArticleService);

  @ViewChild('selectArticle') articleModalComponent?: CustomModalComponent | null = null;

  articleId: number = 0;
  ascendantArticle: boolean = true;
  branchId: number = 0;
  columnSortArticle: string | null = null;
  dataArticle: PagedResponseModel<ArticleSelectionTableModel> = new PagedResponseModel<ArticleSelectionTableModel>();
  filtersArticle: ColumnResponseModel[] = [];
  formStorePrice: FormGroup = new FormGroup({});
  headerArticle: ColumnResponseModel[] = [];
  inputsForArticlesSelect$ = new BehaviorSubject<any>({
    headers: [],
    data: [],
    showCheckbox: true,
    showRecover: true,
    isMultipleSelect: false,
    filters: [],
    actions: [],
  });
  limitArticle: number = 5;
  listArticles: any[] = [];
  listBranches: CustomListDropdown[] = [];
  listStores: CustomListDropdown[] = [];
  nameRentalUnit: string = '';
  openFrom: string = '';
  output$ = new Subject<any>();
  pageArticle: number = 1;
  permissionUpdatable: boolean = false;
  storePriceModel: StorePriceModel = new StorePriceModel();
  subscriptions: Subscription[] = [];
  component = CustomTableSelectComponent;

  constructor(@Optional() private ref: DynamicDialogRef) {
    this.validateForm(new StorePriceModel());
  }

  async ngOnInit() {
    this.subscriptions.push(
      this.output$.subscribe(({ type, payload }) => {
        switch (type) {
          case 'onPageChange':
            this.onPageChangeArticle(payload);
            break;
          case 'onFilterChange':
            this.onFilterChange(payload);
            break;
          default:
            console.warn('Tipo de evento desconocido:', type);
        }
      })
    );

    const input$ = this.config.data?.inputs$;
    if (!input$) { return; }
    this.subscriptions.push(
      input$.subscribe(async (inputs: any) => {
        const { storePriceModel, openFrom } = inputs;
        this.storePriceModel = storePriceModel;
        this.openFrom = openFrom;
      })
    );
    await this.getPermissionProfileByUrl();
    await this.handleOpenModal();
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  branchIdControl(storePrice?: StorePriceModel): number {
    if (storePrice?.branchId) {
      return storePrice.branchId;
    }
    return this.branchId;
  }

  async changeArticle(selectedItems: any[]) {
    const articleId = selectedItems[0]?.articleId;
    if (!articleId) { return; }
    this.storePriceModel.articleId = articleId;
    this.formStorePrice.controls['articleId'].patchValue(articleId);
    try {
      await Promise.all([
        this.getStorePriceByKeys()
      ]);
    } catch (error) {
      console.error('Error al obtener la información del artículo seleccionado.', error);
    }
  }

  async changeStore(storeId: number | null) {
    try {
      this.storePriceModel.storeId = storeId || 0;
      this.formStorePrice.controls['articleId'].patchValue(0);
      this.formStorePrice.controls['unitValue'].patchValue(0);
      if (storeId === null || storeId === 0) {
        this.formStorePrice.controls['storeId'].patchValue(0);
      }
    } catch (error) {
      console.error('Error al cambiar de bodega.', error);
    }
  }

  clearForm() {
    this.storePriceModel = new StorePriceModel();
    this.validateForm(this.storePriceModel);
  }

  close(reload: boolean) {
    const data: ModalObjectType = {};
    if (reload) {
      data.params = {
        reload: true,
        storePriceModel: this.storePriceModel
      };
    }
    this.clearForm();
    this.ref.close(data);
  }

  async getAllLists() {
    try {
      const [branches, stores, articles] = await Promise.all([
        firstValueFrom(this.branchService.getAllBranchesForSelectAsync()),
        firstValueFrom(this.storeService.getAllStoresByBranchForSelectAsync(this.branchId)),
        firstValueFrom(this.articleService.getAllArticlesForSelectAsync()),
      ]);
      this.listBranches = branches;
      this.listStores = stores;
      this.listArticles = articles;
      return true;
    } catch (error) {
      console.error('Error al obtener las listas.', error);
      return false;
    }
  }

  async getArticle(articleId: number | null) {
    const storeId = this.formStorePrice.get("storeId")?.value;
    if (storeId == null || storeId == 0) {
      this.toastHandlerService.showToast('warning', 'Primero debe seleccionar la bodega.');
      this.storePriceModel.articleId = 0;
      setTimeout(() => { this.formStorePrice.controls['articleId'].patchValue(0) }, 100);
      return;
    }
    if (articleId && articleId != 0) {
      try {
        this.storePriceModel.articleId = articleId;
        this.getStorePriceByKeys();
      } catch (error) {
        console.error('Error al asignar el articulo.', error)
      }
    }
  }

  async getArticlesForStorePrice() {
    try {
      this.dataArticle = await firstValueFrom(this.costCenterPriceService.getArticlesForCostCenterPrice(
        this.pageArticle, this.limitArticle, this.columnSortArticle, this.ascendantArticle, this.filtersArticle
      ));
      this.updateInputs();
    } catch (error) {
      console.error('Error al obtener la lista de articulos.', error);
    }
  }

  async getBranch(branchId: number | null, change: boolean) {
    if (branchId && branchId != 0) {
      this.branchId = branchId;
      await this.getAllLists();
      if (change) {
        this.formStorePrice.controls['storeId'].patchValue(0);
        this.formStorePrice.controls['unitValue'].patchValue(0);
        this.formStorePrice.controls['articleId'].patchValue(0);
      }
    } else {
      this.branchId = 0;
    }
  }

  async getColumnsArticle() {
    try {
      if (this.headerArticle.length === 0) {
        this.headerArticle = await firstValueFrom(this.costCenterPriceService.getColumnsForArticle());
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta.', error);
    }
  }

  async getFirstBranchByUserAuthenticatedAsync() {
    try {
      const branch = await firstValueFrom(this.branchService.getFirstBranchByUserAuthenticatedAsync());
      this.getBranch(branch.branchId, true);
    } catch (error) {
      console.error('Error al obtener la primera sucursal del usuario autenticado.', error);
    }
  }

  async getPermissionProfileByUrl() {
    try {
      const url = 'store-price';
      this.permissionUpdatable = await firstValueFrom(this.windowService.getPermissionProfileByUrl(url));
    } catch (error) {
      console.error('Error al cargar permisos.', error);
    }
  }

  async getStorePriceByKeys() {
    try {
      const { storeId, articleId } = this.storePriceModel;
      this.storePriceModel = await firstValueFrom(this.storePriceService.getStorePriceByKeys(this.branchId, storeId, articleId));
      if (this.storePriceModel) {
        this.validateForm(this.storePriceModel);
      }
    } catch (error) {
      console.error('Error al obtener el precio por bodega con claves.', error);
    }
  }

  async handleCreateStorePrice() {
    try {
      await this.getFirstBranchByUserAuthenticatedAsync();
      this.validateForm(this.storePriceModel);
    } catch (error) {
      console.error('Error al preparar los datos para crear un precio por bodega.', error);
    }
  }

  async handleEditStorePrice() {
    this.branchId = this.storePriceModel.branchId;
    await this.getAllLists();
    try {
      if (this.storePriceModel.storePriceId !== 0) {
        this.validateForm(this.storePriceModel);
      } else {
        await this.getStorePriceByKeys();
      }
    } catch (error) {
      console.error('Error al cargar datos para editar el precio por bodega.', error);
    }
  }

  async handleOpenModal() {
    try {
      if (this.openFrom === 'createStorePrice') {
        await this.handleCreateStorePrice();
      } else {
        await this.handleEditStorePrice();
      }
    } catch (error) {
      console.error('Error al procesar los datos del modal para creación o edición.', error);
    }
  }

  async openArticleModal() {
    if (this.openFrom !== "createStorePrice" && this.openFrom !== "editStorePrice") {
      this.toastHandlerService.showToast('warning', 'No está permitido modificar este artículo.', 'Atencion');
      return
    }
    if (this.openFrom === 'editStorePrice') {
      this.toastHandlerService.showToast('warning', 'Solo se puede modificar el Vr.Unitario del precio por bodega.');
      return;
    }
    const storeId = this.formStorePrice.get("storeId")?.value;
    if (storeId == null || storeId == 0) {
      this.toastHandlerService.showToast('warning', 'Primero debe seleccionar la bodega.');
      return
    }
    this.filtersArticle = [];
    await this.getColumnsArticle();
    await this.getArticlesForStorePrice();
    this.updateInputs();
    this.articleModalComponent && this.articleModalComponent.show();
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters.map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index).map((item: any) => item.value);
    this.filtersArticle = this.removeDuplicates(filters);
    this.filtersArticle.forEach((filter: any) => this.processFilter(filter));
    this.updateInputs();
  }

  async onPageChangeArticle(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.pageArticle = event.page;
    this.limitArticle = event.pageSize;
    this.columnSortArticle = event.sortField || null;
    this.ascendantArticle = event.sortOrder === 1;
    this.getArticlesForStorePrice();
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filtersArticle = this.filtersArticle.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filtersArticle.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

  async send() {
    Object.keys(this.formStorePrice.controls).forEach((key) => {
      const control = this.formStorePrice.get(key);
      if (control && control.invalid) {
        console.warn(`${key} es inválido`, control.errors, 'value:', control.value);
      }
    });
    if (this.formStorePrice.valid) {
      const modelStorePrice = this.formStorePrice.getRawValue() as StorePriceModel;
      if (this.storePriceModel.storePriceId === 0) {
        try {
          this.storePriceModel = await firstValueFrom(this.storePriceService.createStorePrice(modelStorePrice));
          this.toastHandlerService.showToast('success', 'El precio por bodega fue creado exitosamente.');
        } catch (error) {
          console.error('Error al crear el precio por bodega.', error);
        }
      } else {
        try {
          this.storePriceModel.unitValue = modelStorePrice.unitValue;
          await firstValueFrom(this.storePriceService.updateStorePrice(this.storePriceModel));
          this.toastHandlerService.showToast('success', 'El precio por bodega fue modificado exitosamente.');
        } catch (error) {
          console.error('Error al actualizar el precio por bodega.', error);
        }
      }
      this.close(true);
      this.updateFormPermision();
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      branchId: {
        required: 'El campo Sucursal es obligatorio.',
        min: 'Debe seleccionar una Sucursal válida.'
      },
      storeId: {
        required: 'El campo Bodega es obligatorio.',
        min: 'Debe seleccionar una Bodega válida.'
      },
      articleId: {
        required: 'El campo Artículo es obligatorio.',
        min: 'Debe seleccionar un Artículo válido.'
      },
      unitValue: {
        required: 'El campo Valor Unitario es obligatorio.',
        min: 'Debe ingresar un valor igual o superior a Cero (0).',
        max: 'No puede ingresar un valor mayor a 99,999,999,999.99 en este campo.'
      }
    };

    Object.keys(controlMessages).forEach((control) => {
      const formControl = this.formStorePrice.get(control);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[control];
        Object.keys(formControl.errors).forEach((errorKey) => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  updateFormPermision() {
    if (!this.permissionUpdatable) {
      this.formStorePrice.disable();
    }
    if (this.openFrom === 'editStorePrice') {
      setTimeout(() => this.formControlStateService.setControlState(this.formStorePrice, ['branchId', 'storeId', 'articleId'], false), 100);
    }
  }

  updateInputs() {
    this.inputsForArticlesSelect$.next({
      headers: this.headerArticle,
      data: this.dataArticle,
      showCheckbox: true,
      showRecover: true,
      isMultipleSelect: false,
      limitPaginator: true,
      filters: this.filtersArticle,
      actions: [
        {
          label: 'Imágenes',
          icon: 'pi pi-image',
          action: 'view-image'
        }
      ]
    });
  }

  validateForm(storePrice: StorePriceModel) {
    this.formStorePrice = this.formBuilder.group({
      branchId: [this.branchIdControl(storePrice), [Validators.required, Validators.min(1)]],
      storeId: [storePrice.storeId, [Validators.required, Validators.min(1)]],
      articleId: [storePrice.articleId, [Validators.required, Validators.min(1)]],
      unitValue: [storePrice.unitValue, [Validators.required, Validators.min(0), Validators.max(99999999999.99)]]
    });
    this.updateFormPermision();
  }

}
